// src/context/AuthContext.js
// Authentification avec session persistante fiable (~4 mois) - NE PAS MODIFIER

import React, { createContext, useState, useEffect, useCallback } from 'react';
import apiClient from '../api/client';

export const AuthContext = createContext();

const SESSION_KEY = 'fynho_session';
const SESSION_DURATION_DAYS = 120; // ~4 mois

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const saveSession = (token, data, type) => {
    const expiresAt = Date.now() + SESSION_DURATION_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(SESSION_KEY, JSON.stringify({ token, data, type, expiresAt }));
  };

  // Restaure la session directement depuis les données sauvegardées localement,
  // SANS appel réseau au démarrage. Ça garantit que la connexion reste active
  // même si un souci temporaire de serveur/réseau survient - NE PAS MODIFIER
  useEffect(() => {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (!raw) {
        setLoading(false);
        return;
      }

      const session = JSON.parse(raw);

      if (!session.expiresAt || session.expiresAt <= Date.now()) {
        // Session réellement expirée (après ~4 mois) → on la retire
        localStorage.removeItem(SESSION_KEY);
        setLoading(false);
        return;
      }

      // Session encore valide → on restaure directement, sans vérification serveur
      if (session.type === 'admin') {
        setAdmin(session.data);
      } else {
        setUser(session.data);
      }
    } catch (error) {
      localStorage.removeItem(SESSION_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const result = await apiClient.post('/endpoints/auth.php?action=login', { email, password });
    saveSession(result.token, result.user, 'user');
    setUser(result.user);
    setAdmin(null);
    return result.user;
  }, []);

  const register = useCallback(async (name, email, phone, password) => {
    const result = await apiClient.post('/endpoints/auth.php?action=register', { name, email, phone, password });
    saveSession(result.token, result.user, 'user');
    setUser(result.user);
    setAdmin(null);
    return result.user;
  }, []);

  const loginAdmin = useCallback(async (email, password) => {
    const result = await apiClient.post('/endpoints/auth.php?action=admin-login', { email, password });
    saveSession(result.token, result.admin, 'admin');
    setAdmin(result.admin);
    setUser(null);
    return result.admin;
  }, []);

  // La déconnexion reste la SEULE façon de retirer la session (bouton "Se déconnecter")
  const logout = useCallback(async () => {
    try {
      await apiClient.post('/endpoints/auth.php?action=logout');
    } catch {
      // On déconnecte localement même si l'appel serveur échoue
    }
    setUser(null);
    setAdmin(null);
    localStorage.removeItem(SESSION_KEY);
  }, []);

  const value = {
    user,
    admin,
    isAuthenticated: !!user,
    isAdminAuthenticated: !!admin,
    loading,
    login,
    register,
    loginAdmin,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};